import { useState } from "react";
import { useModal } from "context";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  CustomDatePicker,
  FormButton,
  MessageBox,
  NumberInput,
} from "reusableComponents";
import styles from "./styles.module.scss";
import { lastMinuteIconRed } from "assets/icons/badges";

const LastMinuteConfirmationModal = ({ onConfirm, id }) => {
  const { closeModal } = useModal();
  const { t } = useTranslation("global");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [discount, setDiscount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Handle discount change
  const handleDiscountChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0) {
      setDiscount(value);
      setValidationErrors((prev) => ({ ...prev, discount: "" }));
    }
  };

  // Handle start date change
  const handleStartDateChange = (date) => {
    setStartDate(date);
    setValidationErrors((prev) => ({ ...prev, startDate: "" }));
  };

  // Handle end date change
  const handleEndDateChange = (date) => {
    setEndDate(date);
    setValidationErrors((prev) => ({ ...prev, endDate: "" }));
  };

  // Handle terms checkbox change
  const handleTermsChange = (checked) => {
    setTermsAccepted(checked);
    setValidationErrors((prev) => ({ ...prev, termsAccepted: "" }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!discount || discount < 1 || discount > 100) {
      errors.discount = t("validation.discountRange");
    }

    if (!startDate) {
      errors.startDate = t("validation.required");
    }

    if (!endDate) {
      errors.endDate = t("validation.required");
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      errors.endDate = t("validation.endDateAfterStartDate");
    }

    if (!termsAccepted) {
      errors.termsAccepted = t("modal.cancelbooking.cguWarning");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleConfirm = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const formData = {
        id, // Pass the `id` from props
        last_minute_reduction: discount,
        start_date: startDate,
        end_date: endDate,
      };

      await onConfirm(formData);
      closeModal();
    } catch (err) {
      setError(t("errors.genericError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${styles.lastMinuteConfirmationModal} ${
        loading ? styles.loading : ""
      }`}
    >
      <div className={styles.header}>
        <img src={lastMinuteIconRed} alt="" className={styles.lastMinuteIcon} />
        <h2 className={styles.lastMinuteTitle}>
          {t("modal.lastminute.title")}
        </h2>
      </div>
      <div className={styles.form}>
        <p className={styles.infoText}>{t("modal.lastminute.text")}</p>
        <p className={styles.labelText}>{t("modal.lastminute.discount")}</p>

        <NumberInput
          required
          labelAbove
          addonText="%"
          value={discount}
          onChange={handleDiscountChange}
          error={validationErrors.discount}
        />

        <p className={styles.labelText}>{t("modal.lastminute.period")}</p>
        <CustomDatePicker
          styled
          label={t("modal.newbooking.fromLabel")}
          labelAbove={false}
          grayLabel={false}
          selectedDate={startDate}
          onDateChange={handleStartDateChange}
        />
        {validationErrors.startDate && (
          <MessageBox type="error" message={validationErrors.startDate} />
        )}
        <CustomDatePicker
          styled
          label={t("modal.newbooking.toLabel")}
          labelAbove={false}
          grayLabel={false}
          selectedDate={endDate}
          onDateChange={handleEndDateChange}
          error={validationErrors.endDate}
        />
        {validationErrors.startDate && (
          <MessageBox type="error" message={validationErrors.endDate} />
        )}
        <p
          className={styles.infoText}
          dangerouslySetInnerHTML={{ __html: t("modal.lastminute.text2") }}
        >
        </p>
        <div className={styles.checkboxContainer}>
          <Checkbox
            isSquare
            label={<a href="/static/cgu">{t("modal.lastminute.acceptTerms")}</a>}
            boldText
            checked={termsAccepted}
            onChange={handleTermsChange}
            error={validationErrors.termsAccepted}
          />
          {validationErrors.termsAccepted && (
            <MessageBox type="error" message={validationErrors.termsAccepted} />
          )}
        </div>
      </div>

      {error && <MessageBox message={error} type="error" />}

      <div className={styles.buttons}>
        <FormButton
          variant="gray"
          onClick={closeModal}
          label={t("buttons.cancel")}
        />
        <FormButton
          variant="submit"
          onClick={handleConfirm}
          disabled={loading}
          label={t("buttons.confirm")}
        />
      </div>
    </div>
  );
};

export default LastMinuteConfirmationModal;
